import React from 'react';
import { clsx } from 'clsx';
import { CheckCircle2, AlertTriangle, Wrench, XOctagon, X } from 'lucide-react';

export type BannerSeverity = 'operational' | 'degraded' | 'maintenance' | 'outage';

export interface StatusBannerProps {
  severity: BannerSeverity;
  message: string;
  onDismiss?: () => void;
}

const severityConfig: Record<
  BannerSeverity,
  { bg: string; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  operational: {
    bg: 'bg-emerald-600 text-white',
    icon: CheckCircle2,
    label: 'Operational',
  },
  degraded: {
    bg: 'bg-amber-500 text-white',
    icon: AlertTriangle,
    label: 'Degraded',
  },
  maintenance: {
    bg: 'bg-blue-600 text-white',
    icon: Wrench,
    label: 'Maintenance',
  },
  outage: {
    bg: 'bg-red-600 text-white',
    icon: XOctagon,
    label: 'Outage',
  },
};

export function StatusBanner({ severity, message, onDismiss }: StatusBannerProps) {
  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div
      className={clsx(
        'w-full flex items-center justify-between px-4 py-2 text-sm font-medium z-30 relative',
        config.bg
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 shrink-0" />
        <span className="font-semibold shrink-0">{config.label}:</span>
        <span className="truncate">{message}</span>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="ml-3 p-0.5 rounded hover:bg-white/20 transition-colors shrink-0"
          aria-label="Dismiss banner"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
